import { createRouter, useRouter } from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { routeTree } from "./routeTree.gen";
import { logClientError } from "./lib/error-logger";

function DefaultErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  useEffect(() => {
    logClientError({
      source: "router.boundary",
      message: error?.message ?? "Unknown render error",
      stack: error?.stack,
    });
  }, [error]);
  return (
    <div style={{ backgroundColor: "#ffffff", color: "#0a0a0a" }} className="flex min-h-screen items-center justify-center px-4">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: "#fee2e2" }}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" style={{ color: "#dc2626" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: "#0a0a0a" }}>Si è verificato un errore</h1>
        <p className="mt-2 text-sm" style={{ color: "#525252" }}>Qualcosa è andato storto. Riprova oppure torna alla home.</p>
        {error?.message && (
          <pre className="mt-4 max-h-40 overflow-auto rounded-md p-3 text-left font-mono text-xs" style={{ backgroundColor: "#f5f5f5", color: "#b91c1c" }}>
            {error.message}
          </pre>
        )}
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium"
            style={{ backgroundColor: "#0a0a0a", color: "#ffffff" }}
          >
            Riprova
          </button>
          <a href="/" className="inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium" style={{ borderColor: "#e5e5e5", backgroundColor: "#ffffff", color: "#0a0a0a" }}>
            Vai alla home
          </a>
        </div>
      </div>
    </div>
  );
}

export const getRouter = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000, refetchOnWindowFocus: false },
    },
  });
  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: DefaultErrorComponent,
  });
  return router;
};

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
